import React from 'react';

import {
  Accordion,
  ContentGroup,
  Heading,
  Link,
  List,
  ListItem,
  Main,
  Paragraph,
  Strong,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { StyledLink } from 'client/components/styledLink/StyledLink';
import { Helmet } from 'react-helmet';

import { TenantDisputesContent } from './TenantDisputesPage.config';
import * as routes from '../manifest';

function TenantDisputesPage() {
  const {
    metaContent,
    backLabel,
    pageTitle,
    goingToCourt,
    talkingToTenant,
    ifThatFails,
    mediation,
    courtEnglandWales,
    courtScotland,
    courtNorthernIreland,
    evictingTenantsAccordion,
    disputeAdviceAccordion,
  } = useContent<TenantDisputesContent>();

  return (
    <Main>
      <Helmet>
        <title>{metaContent.title}</title>
      </Helmet>
      <ContentGroup marginTop="05" marginBottom="none">
        <StyledLink
          iconPosition="left"
          data-testid="back-button-link"
          to={routes.LandlordSupport}
        >
          {backLabel}
        </StyledLink>
      </ContentGroup>
      <Heading as="h1" marginTop="05" size="s7">
        {pageTitle}
      </Heading>
      <Paragraph>{goingToCourt}</Paragraph>
      <Paragraph>{talkingToTenant}</Paragraph>
      <Paragraph>{ifThatFails}</Paragraph>

      <Heading as="h2" size="s4">
        {mediation.title}
      </Heading>
      <Paragraph>
        {mediation.askAProfessional}
        <Link
          data-testid="find-mediator-link"
          href={mediation.findOneHereLinkHref}
          external
        >
          {mediation.findOneHereLinkLabel}
        </Link>
        {mediation.basedInEnglandOrWales}
      </Paragraph>
      <Paragraph>{mediation.northernIreland}</Paragraph>
      <Paragraph>
        <Link
          data-testid="find-scottish-mediator-link"
          href={mediation.findMediatorLinkHref}
          external
        >
          {mediation.findMediatorLinkLabel}
        </Link>
      </Paragraph>
      <Paragraph>
        {mediation.needToUse}
        <Link
          data-testid="scottish-arbitration-service-link"
          href={mediation.scottishArbitrationServiceLinkHref}
          external
        >
          {mediation.scottishArbitrationServiceLinkLabel}
        </Link>
        {mediation.disputeAboutProperty}
      </Paragraph>

      <Heading as="h2" size="s4">
        {courtEnglandWales.title}
      </Heading>
      <Paragraph>{courtEnglandWales.lastResort}</Paragraph>
      <List>
        <ListItem>
          {courtEnglandWales.youNeedToGo}
          <Link
            data-testid="small-claims-court-link"
            href={courtEnglandWales.smallClaimsCourtLinkHref}
            external
          >
            {courtEnglandWales.smallClaimsCourtLinkLabel}
          </Link>
          {courtEnglandWales.repairClaims}
        </ListItem>
        <ListItem>
          {courtEnglandWales.repossession}
          <Link
            data-testid="make-online-claim-link"
            href={courtEnglandWales.onlineClaimLinkHref}
            external
          >
            {courtEnglandWales.onlineClaimLinkLabel}
          </Link>
          {courtEnglandWales.period}
        </ListItem>
      </List>

      <Heading as="h3" size="s3">
        {courtScotland.title}
      </Heading>
      <Paragraph>
        <Strong>{courtScotland.firstTier}</Strong>
      </Paragraph>
      <List>
        {courtScotland.listItems.map((item) => (
          <ListItem key={item.split(' ')[0]}>{item}</ListItem>
        ))}
      </List>
      <Paragraph>
        <Link
          data-testid="housing-property-chamber-scotland-link"
          href={courtScotland.housingChamberLinkHref}
          external
        >
          {courtScotland.housingChamberLinkLabel}
        </Link>
      </Paragraph>

      <Heading as="h3" size="s3">
        {courtNorthernIreland.title}
      </Heading>
      <Paragraph>
        {courtNorthernIreland.tenantDisputes}
        <Link
          data-testid="dispute-resolution-mechanism-link"
          href={courtNorthernIreland.disputeResolutionLinkHref}
          external
        >
          {courtNorthernIreland.disputeResolutionLinkLabel}
        </Link>
        {courtNorthernIreland.reduceCourtCases}
      </Paragraph>

      <Accordion label={evictingTenantsAccordion.label} variation="standalone">
        <Paragraph>{evictingTenantsAccordion.followProcedures}</Paragraph>

        <Heading as="h2" size="s2">
          {evictingTenantsAccordion.fixedTermTitle}
        </Heading>
        <List>
          <ListItem>
            {evictingTenantsAccordion.mustServe}
            <Link
              data-testid="section-21-notice-link"
              href={evictingTenantsAccordion.sectionNoticeLinkHref}
              external
            >
              {evictingTenantsAccordion.sectionNoticeLinkLabel}
            </Link>
            {evictingTenantsAccordion.getPropertyBack}
          </ListItem>
        </List>

        <Heading as="h2" size="s2">
          {evictingTenantsAccordion.weekByWeekTitle}
        </Heading>
        <List marginBottom="none">
          <ListItem>
            {evictingTenantsAccordion.serveThem}
            <Link
              data-testid="section-8-notice-link"
              href={evictingTenantsAccordion.sectionNoticeLinkHref}
              external
            >
              {evictingTenantsAccordion.section8NoticeLinkLabel}
            </Link>
            {evictingTenantsAccordion.brokenTerms}
          </ListItem>
          <ListItem>
            {evictingTenantsAccordion.applyFor}
            <Link
              data-testid="standard-possession-order-link"
              href={evictingTenantsAccordion.standardPossessionLinkHref}
              external
            >
              {evictingTenantsAccordion.standardPossessionLinkLabel}
            </Link>
            {evictingTenantsAccordion.oweRent}
          </ListItem>
          <ListItem>
            {evictingTenantsAccordion.notClaimingUnpaidRent}
            <Link
              data-testid="accelerated-possession-order-link"
              href={evictingTenantsAccordion.acceleratedPossessionOrderLinkHref}
              external
            >
              {evictingTenantsAccordion.acceleratedPossessionOrderLinkLabel}
            </Link>
            {evictingTenantsAccordion.period}
          </ListItem>
          <ListItem>
            {evictingTenantsAccordion.stillDontLeave}
            <Link
              data-testid="warrant-for-possession-link"
              href={evictingTenantsAccordion.warrantForPossessionLinkHref}
              external
            >
              {evictingTenantsAccordion.warrantForPossessionLinkLabel}
            </Link>
            {evictingTenantsAccordion.removeTenants}
          </ListItem>
        </List>
      </Accordion>

      <Accordion
        label={disputeAdviceAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>
          <Link
            data-testid="shelter-link"
            href={disputeAdviceAccordion.shelterLinkHref}
            external
          >
            {disputeAdviceAccordion.shelterLinkLabel}
          </Link>
        </Paragraph>
        <Paragraph>
          <Link
            data-testid="shelter-cymru-link"
            href={disputeAdviceAccordion.shelterCymruLinkHref}
            external
          >
            {disputeAdviceAccordion.shelterCymruLinkLabel}
          </Link>
        </Paragraph>
        <Paragraph>
          <Link
            data-testid="citizens-advice-link"
            href={disputeAdviceAccordion.citizensAdviceLinkHref}
            external
          >
            {disputeAdviceAccordion.citizensAdviceLinkLabel}
          </Link>
        </Paragraph>
        <Paragraph marginBottom="none">
          <Link
            data-testid="civil-legal-advice-link"
            href={disputeAdviceAccordion.civilLegalAdviceLinkHref}
            external
          >
            {disputeAdviceAccordion.civilLegalAdviceLinkLabel}
          </Link>
          {disputeAdviceAccordion.legalAid}
        </Paragraph>
      </Accordion>
    </Main>
  );
}

export default TenantDisputesPage;
